<template>
  <div class="overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
            <input
              type="checkbox"
              :checked="allSelected"
              :indeterminate="someSelected && !allSelected"
              @change="toggleSelectAll"
              class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
            />
          </th>
          <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
            Type
          </th>
          <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Name
          </th>
          <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Date
          </th>
          <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Salvations
          </th>
          <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Baptisms
          </th>
          <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Widows
          </th>
          <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Orphans
          </th>
          <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr
          v-for="mrd in mrds"
          :key="mrd.uuid"
          @click="selectRow(mrd)"
          :class="[
            'cursor-pointer transition-colors',
            selectedMrdUuid === mrd.uuid ? 'bg-indigo-50' : 'hover:bg-gray-50',
            focusedRowUuid === mrd.uuid ? 'ring-2 ring-inset ring-indigo-500' : ''
          ]"
          :tabindex="0"
          @keydown.space.prevent="toggleRowSelection(mrd.uuid)"
          @keydown.enter.prevent="selectRow(mrd)"
          @keydown.up.prevent="focusPreviousRow(mrd)"
          @keydown.down.prevent="focusNextRow(mrd)"
          :ref="el => setRowRef(mrd.uuid, el)"
        >
          <td class="px-3 py-4 whitespace-nowrap" @click.stop>
            <input
              type="checkbox"
              :checked="selectedUuids.includes(mrd.uuid)"
              @change="toggleRowSelection(mrd.uuid)"
              class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
            />
          </td>
          <td class="px-3 py-4 whitespace-nowrap">
            <div class="flex items-center gap-1.5">
              <!-- Person icon -->
              <svg
                v-if="mrd.reportable_type === 'Person'"
                :class="['h-5 w-5', mrd.included_in_tcr ? 'text-green-500' : 'text-gray-400']"
                stroke="none"
                fill="currentColor"
                viewBox="0 0 24 24"
                :title="mrd.reportable_type"
              >
                <path d="M12 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm9 11a1 1 0 0 1-2 0v-2a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v2a1 1 0 0 1-2 0v-2a5 5 0 0 1 5-5h8a5 5 0 0 1 5 5v2z"/>
              </svg>
              <!-- Church icon -->
              <svg
                v-else
                :class="['h-5 w-5', mrd.included_in_tcr ? 'text-green-500' : 'text-gray-400']"
                stroke="none"
                fill="currentColor"
                viewBox="0 0 24 24"
                :title="mrd.reportable_type"
              >
                <path d="M19.57,14.74a1,1,0,0,0,.69.28,1,1,0,0,0,.72-.31,1,1,0,0,0,0-1.41l-2.3-2.22L18.58,11,13,5.61V4.2h.92a1,1,0,0,0,0-2H13V1.56a1,1,0,0,0-2,0V2.2h-.92a1,1,0,0,0,0,2H11V5.61L3.1,13.3a1,1,0,1,0,1.4,1.44L5.24,14v7.13H4.36a1,1,0,0,0,0,2H19.6a1,1,0,0,0,0-2h-.68v-7ZM7.24,21.15V12.07L12,7.42l4.92,4.76v9Z"/>
              </svg>
              <span
                v-if="mrd.included_in_tcr"
                class="text-green-500"
                title="Included in this report"
              >
                <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
              </span>
            </div>
          </td>
          <td class="px-3 py-4 whitespace-nowrap">
            <a
              :href="getReportableLink(mrd)"
              target="_blank"
              @click.stop
              class="text-sm font-medium text-gray-900 hover:text-indigo-600"
            >
              {{ mrd.reportable_name }}
            </a>
          </td>
          <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
            {{ formatDate(mrd.report_date) }}
          </td>
          <td class="px-3 py-4 whitespace-nowrap text-center">
            <MrdDeltaIndicator
              :value="mrd.num_salvations || 0"
              :delta="mrd.delta?.num_salvations"
              class="text-sm"
            />
          </td>
          <td class="px-3 py-4 whitespace-nowrap text-center">
            <MrdDeltaIndicator
              :value="mrd.num_baptisms || 0"
              :delta="mrd.delta?.num_baptisms"
              class="text-sm"
            />
          </td>
          <td class="px-3 py-4 whitespace-nowrap text-center">
            <MrdDeltaIndicator
              :value="mrd.num_widows || 0"
              :delta="mrd.delta?.num_widows"
              class="text-sm"
            />
          </td>
          <td class="px-3 py-4 whitespace-nowrap text-center">
            <MrdDeltaIndicator
              :value="mrd.num_orphans || 0"
              :delta="mrd.delta?.num_orphans"
              class="text-sm"
            />
          </td>
          <td class="px-3 py-4 whitespace-nowrap">
            <span
              :class="[
                'px-2 py-1 text-xs font-medium rounded-full',
                mrd.state === 'validated'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              ]"
            >
              {{ mrd.state === 'validated' ? 'Validated' : 'Pending' }}
            </span>
          </td>
        </tr>
        <tr v-if="mrds.length === 0">
          <td colspan="9" class="px-3 py-8 text-center text-gray-500">
            No ministry report details found
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import MrdDeltaIndicator from './MrdDeltaIndicator.vue'

const props = defineProps({
  mrds: {
    type: Array,
    default: () => []
  },
  selectedUuids: {
    type: Array,
    default: () => []
  },
  selectedMrdUuid: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['select-row', 'toggle-selection', 'select-all', 'deselect-all'])

const rowRefs = ref({})
const focusedRowUuid = ref(null)

const allSelected = computed(() => {
  return props.mrds.length > 0 && props.selectedUuids.length === props.mrds.length
})

const someSelected = computed(() => {
  return props.selectedUuids.length > 0
})

const setRowRef = (uuid, el) => {
  if (el) {
    rowRefs.value[uuid] = el
  }
}

const selectRow = (mrd) => {
  emit('select-row', mrd)
}

const toggleRowSelection = (uuid) => {
  emit('toggle-selection', uuid)
}

const toggleSelectAll = () => {
  if (allSelected.value) {
    emit('deselect-all')
  } else {
    emit('select-all')
  }
}

const getReportableLink = (mrd) => {
  const type = mrd.reportable_type?.toLowerCase()
  const id = mrd.reportable_id
  if (type === 'person') {
    return `/people/${id}`
  } else if (type === 'church') {
    return `/churches/${id}`
  }
  return '#'
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
}

const focusPreviousRow = (currentMrd) => {
  const currentIndex = props.mrds.findIndex(m => m.uuid === currentMrd.uuid)
  if (currentIndex > 0) {
    const prevMrd = props.mrds[currentIndex - 1]
    focusedRowUuid.value = prevMrd.uuid
    rowRefs.value[prevMrd.uuid]?.focus()
  }
}

const focusNextRow = (currentMrd) => {
  const currentIndex = props.mrds.findIndex(m => m.uuid === currentMrd.uuid)
  if (currentIndex < props.mrds.length - 1) {
    const nextMrd = props.mrds[currentIndex + 1]
    focusedRowUuid.value = nextMrd.uuid
    rowRefs.value[nextMrd.uuid]?.focus()
  }
}

// Expose method for parent to focus a specific row
const focusRow = (uuid) => {
  focusedRowUuid.value = uuid
  rowRefs.value[uuid]?.focus()
  rowRefs.value[uuid]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

defineExpose({ focusRow })
</script>
